import { useQuery } from 'react-query';
import { supabase } from '../lib/supabaseClient';

interface PaymentMethod {
  id: string;
  name: string;
  description: string;
  icon: string;
  metadata: {
    useDirectLink?: boolean;
    useDirectPayment?: boolean;
    subscriptionUrls?: Record<string, string>;
    planIds?: Record<string, string>;
  };
}

interface PaymentGateway {
  id: string;
  name: string;
  methods: PaymentMethod[];
}

export const usePaymentMethods = (regionCode: string = 'US') => {
  const { data: methods = [], isLoading, error } = useQuery<PaymentGateway[]>(
    ['payment-methods', regionCode],
    async () => {
      try {
        // Fetch payment methods for the region
        const { data, error } = await supabase
          .rpc('get_region_payment_methods', {
            p_region_code: regionCode
          });

        if (error) throw error;
        
        if (!data || data.length === 0) {
          return [];
        }

        // Group methods by gateway
        const gatewayMap = new Map<string, {
          id: string;
          name: string;
          methods: PaymentMethod[];
        }>();

        data.forEach(method => {
          if (!gatewayMap.has(method.gateway_id)) {
            gatewayMap.set(method.gateway_id, {
              id: method.gateway_id,
              name: method.gateway_name,
              methods: []
            });
          }

          const gateway = gatewayMap.get(method.gateway_id);
          if (gateway) {
            gateway.methods.push({
              id: method.method_id,
              name: method.method_name,
              description: method.method_description,
              icon: method.method_icon,
              metadata: {}
            });
          }
        });

        // Fetch method metadata
        const methodIds = data.map(m => m.method_id);
        const { data: methodsWithMetadata, error: metadataError } = await supabase
          .from('payment_methods')
          .select('id, metadata')
          .in('id', methodIds);

        if (metadataError) throw metadataError;

        // Add metadata to methods
        if (methodsWithMetadata) {
          const metadataMap = new Map<string, any>();
          methodsWithMetadata.forEach(m => {
            metadataMap.set(m.id, m.metadata);
          });

          gatewayMap.forEach(gateway => {
            gateway.methods = gateway.methods.map(method => ({
              ...method,
              metadata: metadataMap.get(method.id) || {}
            }));
          });
        }

        return Array.from(gatewayMap.values());
      } catch (error) {
        console.error('Error fetching payment methods:', error);
        return [];
      }
    },
    {
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 24 * 60 * 60 * 1000 // 24 hours
    }
  );

  return {
    methods,
    isLoading,
    error
  };
};